<template>
  <withTopNav :leftText="configInfo.activityConfigName" class="text-13" fixed>
    <div class="pt-4 relative">
      <ShareLink :title="$t('Activities.treasurebox.LinkDeConvite')" textType="treasurebox" />
    </div>
    <div class="flex flex-col px-4 mt-6">
      <div class="text-[16px] font-[500]">{{ $t('Activities.treasurebox.CompartilhamentoRapido') }}</div>
      <ShareMedia />
    </div>

    <div class="px-4 mt-6">
      <div
        @click="$router.push(`/treasurebox/invitedetail?activityId=${activityId}`)"
        class="flex items-center justify-between text-[15px] font-[600] px-4 py-2.5 bg-[#2C7952]/40 rounded-lg shadow-[0px_4px_4px_0px_#0000001A]"
      >
        <span class="text-white text-[13px]">{{ $t('Activities.treasurebox.MeusSubordinadosDiretos') }}</span>
        <div class="flex items-center gap-1">
          <span class="text-[#FFDF12] text-[15px] leading-none">
            {{ configInfo.validInvitationsNum || 0 }}
          </span>
          <span class="text-[#FFFFFFBF] text-[13px] font-[400]"> {{ $t('Activities.treasurebox.Pessoas') }} </span>
          <Image :src="arrow_right" alt="" class="w-6 h-6" />
        </div>
      </div>
    </div>

    <div class="px-4 mt-4">
      <ul class="grid grid-cols-4 gap-4">
        <li
          v-for="(item, index) in configInfo.details"
          :key="index"
          :class="['relative p-2', getColStartClass(item)]"
        >
          <div class="flex items-center justify-between">
            <div
              class="flex flex-col items-center justify-center"
              @click="
                () => {
                  // 待领取才有recordId
                  if (item.status === 'Awarded') {
                    postReceiveTreaturebox(item.recordId);
                  }
                }
              "
            >
              <img
                :src="iconStatus[item.status] || expired_box"
                alt=""
                class="w-15 h-15 mb-1"
              />
              <span class="text-white/75 text-[11px]">{{ item.userCount }} {{ $t('Activities.treasurebox.Pessoas') }}</span>
              <span
                :class="
                  item.status === 'Expired'
                    ? 'text-white/50 text-[13px]'
                    : 'text-white text-[13px] font-[600]'
                "
              >
                {{
                  item.status === "Expired"
                    ? $t('Activities.treasurebox.Expirado')
                    : formatter().format(item.rewardAmount)
                }}
              </span>
            </div>
            <img
              v-if="item.showSideArrow && !item.isLastRowOfCurrentRow"
              :src="right_down_icon"
              alt=""
              class="w-5 h-5 absolute right-[-15px] top-[45px] transform -translate-y-1/2 z-10"
              :class="{ 'rotate-180': !item.isEvenRow }"
            />
          </div>
          <img
            v-if="item.showDownArrow"
            :src="right_down_icon"
            alt=""
            class="w-5 h-5 absolute bottom-[-12px] left-1/2 transform -translate-x-1/2 rotate-90"
          />
        </li>
      </ul>
    </div>
    <ActivityDesc
      class="mt-4 px-4"
      :title="$t('Activities.treasurebox.DescricaoDaAtividade')"
      :list="configInfo.activityConfigDesc"
    />
  </withTopNav>
</template>

<script setup lang="tsx">
import arrow_right from "./images/arrow_right.png";
import expired_box from "./images/expired_box.png";
import AWARDED_box from "./images/AWARDED_box.png";
import RECEIVED_box from "./images/RECEIVED_box.png";
import right_down_icon from "./images/right_down_icon.png";
import withTopNav from "@/layouts/withTopNav.vue";
import ShareLink from "@a/components/ShareLink/index.vue";
import ShareMedia from "@a/components/ShareMedia/index.vue";
import ActivityDesc from "@/components/ActivityDesc/index.vue";
import { useUserInfo } from "@/store";
import { configDetailById, configDetailByIdWithoutAuth } from "@/api/treasurebox";
import { receiveReward } from "@/api/activity";
import { formatter } from "@/utils";
import { useSystemInfo } from "@/store";
import { useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const iconStatus = {
  Awarded: AWARDED_box,
  Received: RECEIVED_box,
  Expired: expired_box,
};
const route = useRoute();
const activityId = route.query.activityId as string;
const loading = ref(false);
const configInfo = ref<any>({});
// 查询宝箱活动配置
const getConfigDetail = async () => {
  loading.value = true;
  const fn = useUserInfo().isLoggedin ? configDetailById : configDetailByIdWithoutAuth;
  let { code, data } = await fn({
    activityId,
    language: useSystemInfo().language,
  });
  if (code === 1) {
    const configJson = data?.configJson ? JSON.parse(data?.configJson) : {};
    const activityConfigDesc = data?.activityConfigDesc
      ? data?.activityConfigDesc?.split("\n")
      : [];

    const originalDetails = data?.details || [];
    const processedDetails: any[] = [];

    for (let i = 0; i < originalDetails.length; i += 4) {
      const row = originalDetails.slice(i, i + 4);
      const rowNumber = Math.floor(i / 4);

      if (rowNumber % 2 !== 0) {
        processedDetails.push(...row.reverse());
      } else {
        processedDetails.push(...row);
      }
    }
    configInfo.value = {
      ...data,
      configJson,
      activityConfigDesc,
      activityConfigName: data.activityConfigName,
      details: processedDetails,
    };
    configInfo.value.details &&
      configInfo.value.details.forEach((item: any, index: number) => {
        // 计算当前项目所在的行数（从0开始）
        item.row = Math.floor(index / 4);
        // 计算总共需要多少行来显示所有项目
        item.totalRows = Math.ceil(configInfo.value.details.length / 4);
        // 检查是否是网格的最后一行
        item.isLastRow = item.row === item.totalRows - 1; // 是否是最后一排
        // 检查是否是当前行的最后一个项目（第4个位置）
        item.isLastInRow = (index + 1) % 4 === 0;
        // 检查是否是当前行的第一个项目（第1个位置）
        item.isFirstInRow = index % 4 === 0;
        // 检查当前行是否是偶数行（第0、2、4...行）
        item.isEvenRow = item.row % 2 === 0;

        // 修正下箭头显示逻辑：
        // 1. 如果是最后一排，不显示下箭头
        // 2. 如果是偶数排，显示最后一个的下箭头
        // 3. 如果是奇数排，显示第一个的下箭头
        item.showDownArrow =
          !item.isLastRow && // 不是最后一排
          ((item.isEvenRow && item.isLastInRow) ||
            (!item.isEvenRow && item.isFirstInRow));
        // 显示同一行项目之间的水平箭头
        item.showSideArrow = !item.isLastInRow;
        // 处理每一排的对齐方式
        // 判断当前元素是否是其所在行的最后一个元素
        item.isLastRowOfCurrentRow =
          (index + 1) % 4 === 0 || index === configInfo.value.details.length - 1;
        // 计算当前行有多少个元素（处理最后一行可能不满的情况）
        item.itemsInCurrentRow = item.isLastRow
          ? configInfo.value.details.length % 4 || 4
          : 4;
        // 判断是否需要偏移：奇数行且不满4个项目时需要偏移
        item.shouldOffsetRow = !item.isEvenRow && item.itemsInCurrentRow < 4;
      });
  }
  loading.value = false;
};

// 获取宝箱列的开始位置
const getColStartClass = (item: any) => {
  if (
    !item.isEvenRow &&
    item.itemsInCurrentRow < 4 &&
    item.shouldOffsetRow &&
    item.isFirstInRow
  ) {
    if (item.itemsInCurrentRow === 1) return "col-start-4";
    if (item.itemsInCurrentRow === 2) return "col-start-3";
    if (item.itemsInCurrentRow === 3) return "col-start-2";
  }
  return "";
};

// 领奖
const postReceiveTreaturebox = async (id: string) => {
  loading.value = true;
  const { code } = await receiveReward({
    activityId,
    rewardId: id,
    flowType: "TreasureBoxReward",
  });
  if (code === 1) {
    showToast({
      message: t('Activities.treasurebox.RecompensaResgatadaComSucesso'),
      icon: "success",
    });
    getConfigDetail();
  }
  loading.value = false;
};

onMounted(() => {
  showLoadingToast(t('Common.Loading'));
  try {
    getConfigDetail();
  } finally {
    closeToast();
  }
});
</script>

<style lang="scss" scoped></style>
